/**
 * Grafana Faro frontend RUM for the widget (F-3/F-4, MR-69; station-in-meta
 * per F-7/MR-72).
 *
 * Ported from postv2's `src/core/faro.js` with ONE deliberate deviation
 * (per the 2026-07-15 decision, amended 2026-07-16): postv2 does NOT filter
 * `NotAllowedError` (it can't tell an autoplay denial from a real permission
 * failure by message alone). This widget filters it from error reporting —
 * autoplay denial is the dominant benign case for an embedded audio player —
 * but does NOT drop it silently: it counts it as a Faro *measurement* so the
 * ramp isn't flying blind. See `NOT_ALLOWED_MEASUREMENT_TYPE` below for the
 * same ambiguity caveat postv2 documented (a rare clipboard/fullscreen denial
 * with the identical message text would also be counted here — acceptable
 * for a coarse ramp gauge, not acceptable for per-event attribution).
 *
 * ⚠️ Max-effort review finding (folded): a global `window.addEventListener(
 * 'unhandledrejection', ...)` inside `initFaro` is dead code for every
 * NotAllowedError call site that actually exists in this codebase today —
 * `Player.vue`'s `playMedia`/`playLiveStream` and `Menu.vue`'s clipboard copy
 * all already `.catch()` their own rejection (console-only, never
 * unhandled). So `reportNotAllowedIfApplicable` below is exported for THOSE
 * call sites to invoke directly from their own `.catch()` blocks (wired in
 * MR-70) instead of relying on a listener that never fires in practice.
 */
import { initializeFaro, getWebInstrumentations, InternalLoggerLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation, fetchCustomAttributeFunctionWithDefaults } from '@grafana/faro-web-tracing';
import { originFromCandidates } from '../utils/resize';
import { normalizeStation } from '../utils/station';
import { pushTelemetryEvent } from './telemetry';

// Stamp the embed's station onto browser→backend spans, so traces are
// filterable per station in Tempo (`{ span.app.station = "WXYZ" }`), mirroring
// postv2's stampStation. Unlike postv2 (which reads the current route), the
// widget's station is fixed for the embed's lifetime — it's the value passed
// into initFaro, read directly, no separate state to keep in sync.
const stampStation = (span, station) => {
  if (station) {
    span.setAttribute('app.station', station);
  }
};

// Every query parameter the WIDGET itself reads, derived from the `queryParams.*`
// reads in `src/` (utils/parseConfig.js plus app.vue's own). Anything else in the
// iframe's query string did not come from us — see redactPageUrl.
const WIDGET_QUERY_PARAMS = [
  'accent', 'albumart', 'apiDomain', 'autoplay', 'background', 'colors', 'futurishared',
  'gallery', 'hashtags', 'id', 'limit', 'mode', 'mods', 'next', 'page', 'perPage',
  'shownotes', 'smc', 'src', 'station', 'text', 'theme', 'use_m', 'v', 'version',
  'vertical', 'zone',
];

// 🔑 REDACT THE HOST PAGE'S QUERY STRING OUT OF `meta.page.url`.
//
// !263 declined to stamp the host href as an attribute because "Faro would be a NEW
// place it is RETAINED and queryable". Independent review measured that the full
// href was ALREADY in Faro, on EVERY payload, and had been all along: the SDK's
// `createPageMeta` is registered unconditionally and sets `page.url` to the IFRAME's
// location — and `classes/widget.js` builds that location by (a) copying the host
// page's ENTIRE query string into it verbatim and (b) appending
// `src=<encodeURIComponent(document.location.href)>`. So the host's session tokens
// and addresses were being retained twice per payload, in the one field the privacy
// analysis never looked at, while the analysis concluded the risk was avoided.
//
// ⚠️ The obvious remedy — overriding `page` in the Faro config — does NOT work:
// `createPageMeta` is added to `createDefaultMetas` unconditionally and wins. The
// working remedy is `beforeSend`, applied to the item on its way out.
//
// What survives: scheme, host, path, and the widget's OWN parameters, with `src`
// reduced to the host page's ORIGIN AND PATH (see hostPageIdentity — it used to be
// origin-only, which cost per-page attribution). What goes: every parameter we did
// not put there, because it came off the customer's page and is not ours to keep,
// plus the host page's own query string and fragment.

// WHICH PAGE the embed is on: the host page's origin and path, with the query string
// and fragment removed.
//
// 🔑 THE PATH IS KEPT DELIBERATELY (Adrian, 2026-08-20). This used to reduce `src` to a
// bare ORIGIN, which threw away the one dimension a per-embed question needs. It is what
// blocked closing the LCP finding of 2026-08-20: ruralradio.com is 61% of all widget RUM
// and 596 of the 706 samples over 10s, with p75 element_render_delay of 28,347ms against
// 346ms everywhere else — and no telemetry could say which of their pages the slow embeds
// sat on, so "the iframe is off-screen or in a collapsed container on some page" stayed a
// hypothesis instead of a finding.
//
// 🔴 THE QUERY AND HASH STILL GO, because that is where the disclosure actually lived.
// `classes/widget.js` copies the host page's ENTIRE query string into the iframe URL and
// appends `src=<encodeURIComponent(document.location.href)>`, so host session tokens and
// addresses were retained twice per payload. This keeps the path and drops the query and
// the fragment, which carries tokens as readily as a query does.
//
// 🪤 AND IT IS A BOUNDED WIDENING, NOT A CLEAN SEPARATION. An earlier version of this note
// said "a path identifies a page; a query string identifies a session", and a future
// reviewer would lean on that sentence — so it has to be right, and it is not. Two
// measured reasons:
//
//   1. A PATH CAN CARRY A SECRET. Retained verbatim from real traffic:
//      `/account/reset/eyJhbGciOiJIUzI1NiJ9-SECRET` and
//      `/users/adrianowen%40futurimedia.com/profile`. Keeping paths retains those.
//   2. THE `src` VALUE ITSELF CAME OUT OF A QUERY STRING until the last-wins fix below.
//      `get('src')` is first-wins and the loader copies the host page's query in FIRST, so
//      a host page carrying its own `?src=` decided what got retained — a path lifted out
//      of exactly the query string this block promises to drop.
//
// So: this WIDENS what is retained, deliberately, because per-page attribution was the one
// dimension a per-embed question needs and no other field carries it. It does not make the
// retained value safe by category. Treat it as a trade that was made, not a line that was
// drawn.
//
// Validation is INHERITED from originFromCandidates rather than re-implemented: it is
// what already rejects a non-absolute value and, importantly, the string 'null' that
// `URL.origin` yields for an opaque (data:/blob:/about:) origin. Returning '' means the
// caller sets no `src` at all, so an unparseable value fails CLOSED instead of passing
// the raw host href through.
//
// 🪤 A bare origin's pathname is '/', so `origin + pathname` would emit
// `https://host.example/` where the old code emitted `https://host.example` — a SECOND
// label for one page in every group-by, the same double-counting the station
// normalization exists to prevent. Collapse it.
function hostPageIdentity(rawSrc) {
  const origin = originFromCandidates(rawSrc, '');
  if (!origin) return '';
  try {
    const path = new URL(rawSrc).pathname;
    return path === '/' ? origin : `${origin}${path}`;
  } catch (e) {
    // originFromCandidates already parsed it, so this is unreachable — but a throw here
    // would surface inside beforeSend, and telemetry must never surface to the embed.
    return origin;
  }
}

function redactPageUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const kept = new URLSearchParams();
    for (const name of WIDGET_QUERY_PARAMS) {
      if (!u.searchParams.has(name)) continue;
      // 🪤 `src` IS READ LAST-WINS, and `get()` — which is FIRST-wins — was the defect.
      // `formatUrlForIframe` (classes/widget.js) copies the host page's ENTIRE query string
      // into the iframe URL and only THEN appends its own `src=<encoded host href>`. So on
      // a host page that already carries a `src` parameter, `get('src')` returned the
      // CUSTOMER'S value, and this function retained an origin+path lifted out of the very
      // query string the block above promises to drop (measured: a crafted
      // `?src=https%3A%2F%2Fcdn.other.example%2Fsecret%2Fpath%3Ftok%3DSEKRIT` came back out
      // as `https://cdn.other.example/secret/path`). An ordinary UTM-style
      // `?src=newsletter` had the mirror-image effect: unparseable, so NO src was retained
      // and the page attribution was silently absent.
      //
      // Last-wins is correct BY CONSTRUCTION, not by luck — the loader's append is
      // unconditional and final, so the last value is always the one we put there.
      // Deliberately `src`-only: every other name here is ours by convention, and taking
      // the last of them would be a behaviour change nothing asked for.
      const value = name === 'src'
        ? (u.searchParams.getAll('src').pop() || '')
        : u.searchParams.get(name);
      if (name === 'src') {
        const hostPage = hostPageIdentity(value);
        if (hostPage) kept.set('src', hostPage);
      } else {
        kept.set(name, value);
      }
    }
    const query = kept.toString();
    return `${u.origin}${u.pathname}${query ? `?${query}` : ''}`;
  } catch (e) {
    // Not a parseable URL — returning it unchanged would be the failure mode this
    // exists to prevent, so return nothing rather than something unexamined.
    return '';
  }
}

// Faro measurement `type` for the filtered-but-counted NotAllowedError case
// (F-3). A measurement, not an error/event — it never reaches the exception
// stream, only a counted gauge.
export const NOT_ALLOWED_MEASUREMENT_TYPE = 'not_allowed_error';

// Token list for the third-party-globals pattern (ported verbatim from
// postv2 — same third-party actors, gtag/GA, googletag/GAM, __cmp (IAB
// consent), AviviD, silktideCookieBannerManager).
const THIRD_PARTY_GLOBALS = 'gtag|googletag|__cmp|AviviD|silktideCookieBannerManager';

// F-3: widget-tuned IGNORE_ERRORS. Three patterns ported verbatim from
// postv2 (ResizeObserver/Script-error/play-interrupted/third-party-globals),
// PLUS the one deliberate addition vs postv2: autoplay NotAllowedError.
export const IGNORE_ERRORS = [
  // ResizeObserver couldn't drain all notifications in one animation frame;
  // the browser defers the rest (nothing lost). Covers the current Chromium
  // wording and the legacy one.
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
  // Cross-origin script threw and the browser stripped detail for CORS
  // reasons: window.onerror reports the literal "Script error." with no
  // stack/line. The (?=\s|$) lookahead pins the exact sentinel so a real
  // error merely STARTING with "Script error…" still surfaces.
  /^Script error\.?(?=\s|$)/,
  // HTMLMediaElement play/pause race — a new load()/pause() interrupts an
  // in-flight play() promise (AbortError). Expected during normal episode
  // switching. Firefox's differently-worded equivalent is NOT matched (it's
  // reused for real media-fetch failures there).
  /The play\(\) request was interrupted/,
  // Globals injected by third-party ad/analytics/consent scripts (or browser
  // extensions) on the HOST page the widget is embedded in — not our bundle.
  // Matched in both engine wordings: V8/Firefox "<g> is not defined",
  // WebKit/Safari "Can't find variable: <g>". Word-boundaried so a longer
  // identifier containing a token still surfaces.
  new RegExp(
    `\\b(?:${THIRD_PARTY_GLOBALS}) is not defined\\b` +
      `|Can't find variable: (?:${THIRD_PARTY_GLOBALS})\\b`
  ),
  // Autoplay denial (DOMException NotAllowedError) — the dominant benign
  // error for an embedded audio player under strict autoplay policies.
  // Deliberately broad (matches ANY NotAllowedError message, not just the
  // autoplay wording) because Faro's matched string for this DOMException is
  // `${name}: ${message}` and the message text is identical across autoplay/
  // clipboard/fullscreen denials (see postv2's faro.js — same ambiguity,
  // opposite conclusion). Counted via NOT_ALLOWED_MEASUREMENT_TYPE below so
  // the ramp isn't blind to it.
  /^NotAllowedError:/,
];

// Duck-typing alone (`reason.name === 'NotAllowedError'`) would let any plain
// object/custom Error with that name slip through as a real autoplay/
// permission denial. Real browsers only ever produce this name on a genuine
// DOMException, so require that too where the class exists at all (a
// non-browser/minimal test environment without a global DOMException skips
// the check rather than false-negative on every input).
function isNotAllowedRejection(reason) {
  if (!reason || reason.name !== 'NotAllowedError') return false;
  if (typeof DOMException !== 'undefined' && !(reason instanceof DOMException)) return false;
  return true;
}

// F-3: count (never report) a NotAllowedError-shaped rejection as a Faro
// measurement. Exported for callers OUTSIDE this module — the actual
// NotAllowedError call sites (Player.vue's play(), Menu.vue's clipboard
// copy) already `.catch()` their own rejection (see the module doc above),
// so counting has to happen AT those call sites, not via a global
// `unhandledrejection` listener here (that never fires for an already-caught
// rejection). Reads the global `window.faro` instance (mirroring
// `core/telemetry.js`'s established no-op-safe pattern) since a component
// like Player.vue has no direct reference to the instance `initFaro` returns
// — that's scoped to app.vue's own call. Never throws to the caller.
//
// `skipDedupe: true`: faro-web-sdk's `makeCoreConfig` defaults `dedupe: true`
// (verified against the installed SDK — NOT faro-core's raw default, which
// has none), and every call here pushes an identical `{type, values}` shape
// for a given station — without this, only the FIRST NotAllowedError per
// station would ever be counted; every repeat would be silently deduped.
export function reportNotAllowedIfApplicable(error, station) {
  try {
    const w = typeof window !== 'undefined' ? window : undefined;
    const faro = w && w.faro;
    if (faro && faro.api && typeof faro.api.pushMeasurement === 'function' && isNotAllowedRejection(error)) {
      faro.api.pushMeasurement(
        {
          type: NOT_ALLOWED_MEASUREMENT_TYPE,
          values: { count: 1 },
        },
        // context MUST ride the OPTIONS arg: faro-core reads `context` there and
        // overwrites payload.context with it, so a context passed in the payload
        // arg is silently discarded and per-station attribution is lost
        // (verified against the installed SDK's api/measurements/initialize.js).
        //
        // 🔴 normalizeStation, and this site is WORSE than the general case (review item
        // S11). Twelve call sites hand this the customer's embed-code spelling, and
        // `skipDedupe: true` exists precisely so per-station repeats are all counted — so a
        // station whose embeds are split across `KTICAM` and `kticam` had its autoplay-denial
        // rate reported as two smaller rates, and the obvious query ("which stations are
        // worst for blocked autoplay?") returned neither. Same defect as the widget_layout
        // stamp, on the one measurement whose whole point is counting repeats.
        {
          skipDedupe: true,
          context: normalizeStation(station) ? { station: normalizeStation(station) } : undefined,
        }
      );
    }
  } catch (e) {
    /* telemetry must never surface to the embed */
  }
}

// 🔑 THE RETRY EVENT, in ONE place for all three players.
//
// v2 (D-165), v3 and v4 all report the same thing: a listener met a dead player and
// asked for another go, and then whether that go worked. Three inline copies of an
// attribute set is where a schema drifts — one component gains a field, the dashboard
// stops comparing like with like, and nobody finds out because every copy still
// "works". This module already holds the other cross-version reporter
// (`reportNotAllowedIfApplicable`, which shipped v4-ONLY and was the ninth one-of-N
// defect in this codebase), so the precedent for where a shared reporter lives is set.
//
// Three phases, one event name, so the SUCCESS RATE is a single query rather than a
// join: `attempted` when the listener taps, then exactly one of `recovered` /
// `failed`. A count on its own cannot tell a fix that works from one that fires just
// as often and fails just as often.
//
// 🪤 NO URL RIDES THIS EVENT, and that is deliberate rather than an omission. D-164
// stripped the query string from the engine's own failure events because
// `hls_engine`'s suppression key already declares that per-resolve CDN tokens live
// there — and the event body was forwarding the raw url anyway, so the one field the
// module named as sensitive was the one field shipped to Faro. `source_kind`,
// `live` and `player_version` carry the attribution that matters and cannot carry a
// token. `station` is stamped onto every event by core/telemetry.
//
// Values are strings because Faro attributes are `Record<string, string>`; a boolean
// arrives at the collector coerced anyway, and coercing here keeps the two sides
// honest.
export function reportPlayerRetry(phase, { playerVersion, sourceKind, live }) {
  pushTelemetryEvent('widget_live_retry', {
    phase,
    player_version: playerVersion,
    source_kind: sourceKind,
    live: live ? 'true' : 'false',
  });
}

// F-4: derive the Faro collector URL + environment + version at RUNTIME from
// the resolved apiDomain — one built bundle (one STATIC_APP_VERSION hash) is
// shared across every cluster, so these can never be baked in at build time.
//
// environment: `<cluster>.futurimedia.com` -> "<cluster>", with the two prod
// aliases ("post" and "postv4") folded to "prod" — a DELIBERATE widget-only
// deviation: postv2 emits the raw `host.split('.')[0]` with no fold, so the
// widget reports environment="prod" while the in-app postv2 player reports
// "post"/"postv4" into the same prod cluster's Faro.
// version: passed through from buildHash — the widget's own `<branch>-<sha7>`
// identity, BAKED IN at build time (`__WIDGET_BUILD_VERSION__`, read by
// src/config/buildVersion.js). It used to be fetched at runtime from the now
// DEPRECATED WEBWIDGETV2-VERSION.txt marker, which 404'd on both prod hostnames
// and made this fall back to 'dev' for 107,231 of 107,233 measured RUM events;
// the fallback stays — it is what a dev-server or test build correctly reports —
// but it is no longer the production answer.
//
// A falsy apiDomain returns an EMPTY faroUrl (never a malformed
// `https:///-/faro/collect`) so the caller's own `if (faroUrl)` no-collector
// guard (F-5) actually engages instead of silently constructing garbage.
export function deriveFaroConfig(apiDomain, buildHash) {
  const host = apiDomain || '';
  if (!host) {
    return { faroUrl: '', environment: 'unknown', version: buildHash || 'dev' };
  }
  const label = host.split('.')[0] || 'unknown';
  const environment = label === 'post' || label === 'postv4' ? 'prod' : label;
  return {
    faroUrl: `https://${host}/-/faro/collect`,
    environment,
    version: buildHash || 'dev',
  };
}

// Initialize Grafana Faro frontend RUM for the widget (page-loads, JS errors,
// web-vitals, browser spans). Callers (F-5/MR-70) only invoke this when a
// collector URL exists AND the visitor isn't a synthetic agent — so a
// bot/no-collector embed never reaches this module's network path at all.
//
// The collector is same-origin per cluster (Varnish proxies /-/faro/collect
// to the node-local Alloy faro.receiver for the IN-APP player; the embedded
// widget's own origin differs — see F-6's CORS follow-up), so this mirrors
// postv2's initFaro shape with the widget's own app name + station stamping.
export function initFaro(url, {
  version, station, environment, hostOrigin,
} = {}) {
  // MR-72 (F-7): station used to ride ONLY the tracing spans (stampStation
  // below) — the fetch/xhr instrumentation's derived Faro events, meaning
  // ~20 of ~22 RUM signals per pageview (web-vitals, resource timing,
  // navigation, session_start/resume) carried no station at all.
  //
  // ⚠️ First attempt put station in `app` meta — WRONG, empirically refuted
  // against the real avow collector: the wire payload DID carry
  // `meta.app.station` (captured the raw POST body), but Alloy's
  // `faro.receiver` decodes `app` (like `sdk`/`browser`/`os`/`page`/
  // `session`'s non-attributes fields) against a fixed known-fields schema
  // and silently drops anything else — a curl probe posting a bespoke
  // top-level `meta.station` was dropped too. Only the SDK's genuinely
  // free-form `attributes` bags (on `user`/`session`/`page`) survive, proven
  // by 3 curl probes each landing in Loki as `<category>_attr_<key>`.
  // `session.attributes` was ruled out too: the installed SDK's session
  // instrumentation + both sessionManager strategies call
  // `faro.api.setSession()` on their own for rotation/persistence, which
  // REPLACES the whole `session` meta object — an attribute stashed there
  // would be silently wiped on the next rotation. `user` meta has no such
  // internal auto-caller anywhere in the SDK (only `setSession` appears in
  // any default instrumentation), so `user.attributes` is the one slot
  // nothing else will ever silently clobber. Lands in Loki as
  // `user_attr_station`, not `app_station`. Omitted entirely (no `user` key
  // at all) when neither value is given, mirroring stampStation's own
  // `if (station)` guard.
  //
  // 🪤 AND THEN !263 REPEATED THE BUG THIS BLOCK IS THE POST-MORTEM FOR.
  // `host_origin` shipped through `core/telemetry.js`'s `withEmbedContext`, which
  // decorates `pushEvent` and nothing else — so it rode 4 hand-pushed events while
  // the exception stream, measurements, logs and every web-vital carried none, and
  // the commit subject said "on every Faro event". That is the identical ~20-of-22
  // shape, with the remedy written out immediately above it. Measured against the
  // installed SDK: pushEvent → station ✓ host_origin ✓; pushError / pushMeasurement
  // / pushLog → station ✓ host_origin ✗. It rides `user.attributes` now, so the
  // claim is true and buffered-before-init events no longer matter for this field.
  // 🔴 FAIL CLOSED WITHOUT A COLLECTOR. An origin nobody has wired up should produce NO
  // telemetry rather than a console full of CORS errors. Both call sites already test
  // `faroUrl` first; asserting it here means a future one cannot forget to.
  if (!url) return null;

  // 🔑 NORMALIZE ONCE, HERE, so both stamps THIS FUNCTION owns inherit it —
  // user.attributes AND the tracing spans. Normalising at the two separately is how one of
  // them gets missed: the station rode both in the customer's own embed-code casing, so
  // MEASURED on postv4 over 13h, `KTICAM` (6,195 events) and `kticam` (1,957) were two
  // labels for one station in every per-station aggregate, and neither was the real number.
  // api.js already lowercased the station for request paths, so this makes the RUM label
  // and the URL agree by construction rather than by coincidence. See src/utils/station.js.
  //
  // 🪤 "SO ANY CALLER INHERITS IT" WOULD BE FALSE, AND THAT PHRASING SHIPPED ONCE. There are
  // FIVE paths that put a station into telemetry, not the three an earlier telling counted,
  // and normalising here reaches only the two above:
  //
  //   1. `user.attributes.station` — here
  //   2. the tracing-span stamp     — here
  //   3. `core/telemetry.js`'s module-level stamp (`setTelemetryStation`) — normalises
  //      itself
  //   4. `reportNotAllowedIfApplicable`'s measurement `context` — a SEPARATE call site in
  //      this file, taking the raw value from twelve callers; it normalises itself now
  //      (review item S11), and did not before
  //   5. a caller that supplies its own `station` KEY to `pushTelemetryEvent` —
  //      `withEmbedContext` guards on hasOwnProperty and deliberately never overwrites it,
  //      so such a caller bypasses (3) entirely. `widget_layout` in app.vue is exactly
  //      that caller, and it shipped the raw-cased station: 49.7% of those events carried
  //      an uppercase label while `user_attr_station` on the same event was lowercase
  //      (review item M1).
  //
  // The rule that is actually true: EVERY SITE THAT COMPOSES A STATION LABEL NORMALISES AT
  // THAT SITE. There is no inheritance to rely on.
  const stationLabel = normalizeStation(station);

  const userAttributes = {};
  if (stationLabel) userAttributes.station = stationLabel;
  if (hostOrigin) userAttributes.host_origin = hostOrigin;
  const hasUserAttributes = Object.keys(userAttributes).length > 0;

  // 🔴 FARO BEING UNREACHABLE IS NOT A HARD FAIL. The widget runs on third-party pages, and
  // a collector that is down, ad-blocked (routine for RUM endpoints) or answering without
  // CORS must cost the customer nothing. The principle was already written down at main.js's
  // degraded path — "observability must never surface to the embed" — but it lived in the
  // CALLERS, so it held only where one remembered to wrap this. It belongs here, where every
  // call site inherits it.
  //
  // Returns null rather than rethrowing, so a caller can tell the difference between "no
  // telemetry" and "telemetry"; nothing is required to look.
  let faro = null;
  try {
    faro = initializeFaro({
      url,
      app: {
        name: 'webwidget',
        version: version || 'dev',
        environment,
      },
      user: hasUserAttributes ? { attributes: userAttributes } : undefined,
      ignoreErrors: IGNORE_ERRORS,
    // 🪤 THE OBSERVED FAILURE THREW NOTHING. The collector answered 202 and the browser
    // blocked READING the response for want of Access-Control-Allow-Origin, so the SDK's OWN
    // logger printed the wall of red — which no try/catch can reach. An embed shares its
    // console with the customer's site, so a telemetry problem that fills it becomes a
    // support ticket about our widget for something that broke nothing.
      internalLoggerLevel: InternalLoggerLevel.OFF,
    // See redactPageUrl. Applied on the way out because the SDK's page meta is
    // registered unconditionally and cannot be overridden from config. Mutating a
    // copy of `meta.page` rather than the item keeps this a projection, not a hook
    // with opinions — and it returns the item, never null, so this can never
    // become an accidental sampler.
    // NB Object.assign, not object spread — this repo's eslint parser is ES2017
    // and rejects `...obj` in an object literal (build.target is es2015).
      beforeSend: (item) => {
        try {
          const pageUrl = item && item.meta && item.meta.page && item.meta.page.url;
          if (!pageUrl) return item;
          const page = Object.assign({}, item.meta.page, { url: redactPageUrl(pageUrl) });
          const meta = Object.assign({}, item.meta, { page });
          return Object.assign({}, item, { meta });
        } catch (e) {
          return item; // telemetry must never surface to the embed
        }
      },
      instrumentations: [
      // captureConsole:false — never ship console.warn/error payloads to Loki
      // from a page we don't control (arbitrary third-party host page).
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation({
          instrumentationOptions: {
            fetchInstrumentationOptions: {
              applyCustomAttributesOnSpan: fetchCustomAttributeFunctionWithDefaults((span) =>
              stampStation(span, stationLabel)
            ),
            },
            xhrInstrumentationOptions: {
              applyCustomAttributesOnSpan: (span) => stampStation(span, stationLabel),
            },
          },
        }),
      ],
    });
  } catch (e) {
    // Swallowed on purpose and NOT logged: the SDK's own logger is already silenced above
    // for exactly this reason, and re-printing here would reintroduce the noise into the
    // embedding page. A null return is the whole signal.
    faro = null;
  }

  return faro;
}
